'use client';

import { useEffect, useState } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Tag, Bell, BedDouble, CalendarCheck, ShieldCheck, Headset, 
  Phone, Mail, Clock, ChevronRight, Shield, MessageCircle,
  Star, Heart, CheckCircle, Coffee, Utensils, Wifi, Car, Sun,
  MapPin, Smile, Gift, Users, Sparkles, Award, LifeBuoy
} from 'lucide-react';
import siteTheme from '@/lib/theme';
import type { SiteSettings } from '@/lib/cms/settings';
import type { BookingModalData } from '@/lib/cms';
import Image from 'next/image';

const { fonts, colors } = siteTheme;

interface BookingContactModalProps {
  settings: SiteSettings | null;
  modalData: BookingModalData | null;
}

const DEFAULT_BENEFITS = [
  {
    icon: <Tag className="text-[#b5935b]" size={22} strokeWidth={1.5} />,
    title: 'Exclusive Rates',
    subtitle: 'Best price guaranteed'
  },
  {
    icon: <Bell className="text-[#b5935b]" size={22} strokeWidth={1.5} />,
    title: 'Personalized Assistance',
    subtitle: 'Tailored to your needs'
  },
  {
    icon: <BedDouble className="text-[#b5935b]" size={22} strokeWidth={1.5} />,
    title: 'Best Room Choices',
    subtitle: 'Handpicked for you'
  },
  {
    icon: <CalendarCheck className="text-[#b5935b]" size={22} strokeWidth={1.5} />,
    title: 'Flexible Support',
    subtitle: 'Hassle-free changes'
  },
  {
    icon: <ShieldCheck className="text-[#b5935b]" size={22} strokeWidth={1.5} />,
    title: 'Instant Confirmation',
    subtitle: 'Quick & reliable'
  },
  {
    icon: <LifeBuoy className="text-[#b5935b]" size={22} strokeWidth={1.5} />,
    title: 'Dedicated Support',
    subtitle: 'Before & during stay'
  }
];

const getIcon = (iconName: string) => {
  const iconProps = { className: "text-[#b5935b]", size: 22, strokeWidth: 1.5 };
  
  switch (iconName) {
    case 'Bell': return <Bell {...iconProps} />;
    case 'BedDouble': return <BedDouble {...iconProps} />;
    case 'CalendarCheck': return <CalendarCheck {...iconProps} />;
    case 'ShieldCheck': return <ShieldCheck {...iconProps} />;
    case 'Headset': return <Headset {...iconProps} />;
    case 'Phone': return <Phone {...iconProps} />;
    case 'Mail': return <Mail {...iconProps} />;
    case 'Clock': return <Clock {...iconProps} />;
    case 'Star': return <Star {...iconProps} />;
    case 'Heart': return <Heart {...iconProps} />;
    case 'CheckCircle': return <CheckCircle {...iconProps} />;
    case 'Coffee': return <Coffee {...iconProps} />;
    case 'Utensils': return <Utensils {...iconProps} />;
    case 'Wifi': return <Wifi {...iconProps} />;
    case 'Car': return <Car {...iconProps} />;
    case 'Sun': return <Sun {...iconProps} />;
    case 'MapPin': return <MapPin {...iconProps} />;
    case 'Smile': return <Smile {...iconProps} />;
    case 'Gift': return <Gift {...iconProps} />;
    case 'Users': return <Users {...iconProps} />;
    case 'Sparkles': return <Sparkles {...iconProps} />;
    case 'Award': return <Award {...iconProps} />;
    case 'Support': return <LifeBuoy {...iconProps} />;
    case 'Tag':
    default:
      return <Tag {...iconProps} />;
  }
};

export default function BookingContactModal({ settings, modalData }: BookingContactModalProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const isOpen = mounted && searchParams.get('contact-booking') === 'true';

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const closeModal = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('contact-booking');
    router.push(pathname + (params.toString() ? `?${params.toString()}` : ''), { scroll: false });
  };

  if (!isOpen) return null;

  const phone = settings?.phone || '+91 96268 04925';
  const whatsappNumber = settings?.whatsapp_number || phone;
  const email = settings?.booking_modal_email || 'hello@sandyresort.in';
  
  const telLink = `tel:${phone.replace(/\s/g, '')}`;
  const waLink = `https://wa.me/${whatsappNumber.replace(/\D/g, '')}?text=${encodeURIComponent(
    settings?.whatsapp_message || "Hello! I'd like to enquire about a booking."
  )}`;

  const title = modalData?.title || 'Experience the Best of Sandy Resort for Less';
  const description = modalData?.description || "Book directly with our reservation team and enjoy exclusive benefits that are only available when you book with us.";
  
  const scriptTitle = modalData?.script_title || 'Book Direct & Save More!';
  const scriptSubtitle = modalData?.script_subtitle?.replace(/\\n/g, '\n') || 'Better rates. Better service.\nA better stay.';

  const benefits = modalData?.benefits?.length 
    ? modalData.benefits.map(b => ({
        icon: getIcon(b.icon),
        title: b.title,
        subtitle: b.subtitle
      }))
    : DEFAULT_BENEFITS;

  const imageUrl = modalData?.image?.url || "https://images.unsplash.com/photo-1571896349842-33c89424de2d?q=80&w=1200&auto=format&fit=crop";

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-md"
          onClick={closeModal}
        />

        {/* Modal Container */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.98 }}
          transition={{ type: 'spring', damping: 28, stiffness: 200 }}
          className="relative flex max-h-[98dvh] w-full max-w-[1100px] flex-col overflow-y-auto rounded-[24px] bg-white shadow-[0_30px_80px_rgba(0,0,0,0.3)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-500 shadow-md transition-transform hover:scale-110 hover:text-gray-800 sm:right-6 sm:top-6"
            aria-label="Close modal"
          >
            <X size={20} strokeWidth={2.5} />
          </button>

          {/* TOP SECTION: White Background */}
          <div className="grid gap-8 p-6 pb-8 sm:p-10 sm:pb-12 lg:grid-cols-[1.2fr_1fr]">
            
            {/* Left Content */}
            <div className="flex flex-col pt-2">
              <div className="mb-8 flex flex-col items-start">
                <h4 
                  className="mb-4 inline-block border-b-2 border-[#b5935b] pb-1 text-lg text-[#0d2f44]"
                  style={{ fontFamily: fonts.serif }}
                >
                  Direct Booking
                </h4>
                
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#f2eadc] bg-[#fcf9f2] px-4 py-1.5">
                  <ShieldCheck size={16} className="text-[#b5935b]" />
                  <span 
                    className="text-xs font-bold uppercase tracking-widest text-[#b5935b]"
                    style={{ fontFamily: fonts.sans }}
                  >
                    Exclusive Direct Booking Benefits
                  </span>
                </div>

                <h2 
                  className="mb-4 text-4xl leading-[1.15] text-[#0d2f44] sm:text-[2.75rem]"
                  style={{ fontFamily: fonts.serif }}
                >
                  {title}
                </h2>
                <p 
                  className="max-w-[90%] text-base leading-relaxed text-gray-500 sm:text-[1.1rem]"
                  style={{ fontFamily: fonts.sans }}
                >
                  {description}
                </p>
              </div>

              {/* Benefits Grid */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
                {benefits.map((b, i) => (
                  <div 
                    key={i} 
                    className="flex flex-col items-center justify-center rounded-2xl border border-gray-100 bg-white p-4 text-center shadow-[0_2px_10px_rgba(0,0,0,0.02)] transition-shadow hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)]"
                  >
                    <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-[#fcf9f2]">
                      {b.icon}
                    </div>
                    <span 
                      className="mb-1 text-sm font-semibold text-[#0d2f44]"
                      style={{ fontFamily: fonts.sans }}
                    >
                      {b.title}
                    </span>
                    <span 
                      className="text-xs text-gray-500"
                      style={{ fontFamily: fonts.sans }}
                    >
                      {b.subtitle}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Image Container */}
            <div className="relative hidden min-h-[400px] w-full lg:block">
              <div className="relative h-full w-full overflow-hidden rounded-3xl">
                {/* Fallback beautiful resort image */}
                <Image 
                  src={imageUrl} 
                  alt="Sandy Resort View" 
                  fill 
                  className="object-cover"
                />
                
                {/* Overlapping Floating Box */}
                <div className="absolute bottom-6 left-6 right-6 overflow-hidden rounded-2xl bg-[#0d2f44]/95 p-8 text-center shadow-2xl backdrop-blur-sm">
                  <h3 
                    className="mb-3 text-[2.2rem] text-[#d4cbb4]"
                    style={{ fontFamily: fonts.script }}
                  >
                    {scriptTitle}
                  </h3>
                  <p 
                    className="whitespace-pre-wrap text-base text-white/90"
                    style={{ fontFamily: fonts.sans }}
                  >
                    {scriptSubtitle}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* BOTTOM SECTION: Light Grey Background */}
          <div className="bg-[#f9fafb] p-6 sm:p-10">
            <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-center">
              
              {/* Contact Info */}
              <div className="flex flex-col">
                <div className="mb-6 text-center lg:text-left">
                  <h4 
                    className="mb-3 text-2xl text-[#0d2f44]"
                    style={{ fontFamily: fonts.serif }}
                  >
                    Contact Our Reservation Team
                  </h4>
                  <div className="mx-auto mb-4 h-0.5 w-12 bg-[#b5935b] lg:mx-0" />
                  <p 
                    className="text-[1.05rem] text-gray-600"
                    style={{ fontFamily: fonts.sans }}
                  >
                    We're here to help you plan the perfect stay.<br className="hidden sm:block" />
                    Call, WhatsApp, or email us anytime.
                  </p>
                </div>

                <div className="flex flex-col items-center justify-center gap-6 sm:flex-row sm:items-start sm:gap-8 lg:justify-start">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#0d2f44] text-white">
                      <Phone size={20} />
                    </div>
                    <span className="mb-1 text-sm font-semibold text-gray-500" style={{ fontFamily: fonts.sans }}>Mobile</span>
                    <span className="font-bold text-[#0d2f44]" style={{ fontFamily: fonts.sans }}>{whatsappNumber}</span>
                  </div>
                  
                  <div className="hidden h-16 w-px bg-gray-200 sm:block" />

                   <div className="flex flex-col items-center text-center">
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#0d2f44] text-white">
                      <Phone size={20} />
                    </div>
                    <span className="mb-1 text-sm font-semibold text-gray-500" style={{ fontFamily: fonts.sans }}>Tel</span>
                    <span className="font-bold text-[#0d2f44]" style={{ fontFamily: fonts.sans }}>{phone}</span>
                  </div>
                  
                  <div className="hidden h-16 w-px bg-gray-200 sm:block" />
                 

                  <div className="flex flex-col items-center text-center">
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#0d2f44] text-white">
                      <Mail size={20} />
                    </div>
                    <span className="mb-1 text-sm font-semibold text-gray-500" style={{ fontFamily: fonts.sans }}>Email</span>
                    <span className="font-bold text-[#0d2f44]" style={{ fontFamily: fonts.sans }}>{email}</span>
                  </div>

                  
                </div>
              </div>

              {/* Action Cards */}
              <div className="rounded-3xl bg-[#eef5f2] p-6 sm:p-8">
                <h4 
                  className="mb-2 text-xl text-[#0d2f44]"
                  style={{ fontFamily: fonts.serif }}
                >
                  Prefer a quick chat?
                </h4>
                <p 
                  className="mb-6 text-[0.95rem] text-gray-600"
                  style={{ fontFamily: fonts.sans }}
                >
                  Connect with us instantly on WhatsApp<br />or give us a call.
                </p>
                
                <div className="flex flex-col gap-3">
                  <a
                    href={telLink}
                    className="flex w-full items-center justify-between rounded-xl bg-[#0d2f44] px-6 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-md transition-colors hover:bg-[#16384c]"
                    style={{ fontFamily: fonts.sans }}
                  >
                    <div className="flex items-center gap-3">
                      <Phone size={18} />
                      Call Now
                    </div>
                    <ChevronRight size={18} className="text-white/70" />
                  </a>

                  <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center justify-between rounded-xl bg-[#1dbd56] px-6 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-md transition-colors hover:bg-[#19a54a]"
                    style={{ fontFamily: fonts.sans }}
                  >
                    <div className="flex items-center gap-3">
                      <MessageCircle size={18} />
                      WhatsApp Us
                    </div>
                    <ChevronRight size={18} className="text-white/70" />
                  </a>
                </div>
              </div>
              
            </div>
          </div>

          {/* Very Bottom Footer */}
          <div className="flex items-center justify-center gap-2 bg-[#f9fafb] pb-6 pt-2 text-[#0d2f44]">
            <Shield size={18} className="text-[#b5935b]" />
            <span style={{ fontFamily: fonts.sans, fontSize: '0.95rem' }}>
              Your stay. Our priority. Let's make it <span style={{ fontFamily: fonts.script, fontSize: '1.4rem', color: '#b5935b', marginLeft: '2px' }}>memorable.</span>
            </span>
          </div>

        </motion.div>
      </div>
    </AnimatePresence>
  );
}
